<template>
  <NuxtLink v-if="resolvedTo"
    :to="resolvedTo"
    :active-class="activeClass"
    :append="append"
    :event="event"
    :exact="exact"
    :exact-active-class="exactActiveClass"
    :replace="replace"
    :prefetch="prefetch"
    :title="title"
    :rel="rel"
    :target="target"
    @click="handleClickEvent"
  >
    <slot></slot>
  </NuxtLink>
  <a v-else
    :href="resolvedHref"
    :title="title"
    :rel="rel"
    :target="target"
    @click="handleClickEvent"
  >
    <slot></slot>
  </a>
</template>

<script setup lang="ts">
import { useDYIsAliasDomain } from '~/modules/nuxt3-module-doyac/composables/config'

const props = withDefaults(defineProps<{
  to? :string,
  href? : string
  title? : string
  target? : '_self'|'_blank'
  rel? : string

  // <router-link> props
  activeClass? : string
  append? : boolean
  /**
   * click
   */
  event? : string|[]
  exact? : boolean
  exactActiveClass? : string
  replace? : boolean

  // <nuxt-link> props
  prefetch? : boolean
}>(),{
  target: () => '_self',
  append: () => false,
  event: () => 'click',
  exact: () => false,
  replace: () => false,
  prefetch: () => false
})

const emit = defineEmits<{
  (e: 'click', v: Event): void
}>()

const route = useRoute()

// [2026-06-05] 차단 우회용 별칭 도메인(web-mfg) 대응 — DYLink 한 곳에서 링크 정규화.
// ★ 별칭 도메인(web-mfg 등)으로 접속했을 때만 동작한다. 일반 도메인(www.myfirstguide.com)에서는
//   creator.myfirstguide.com 처럼 "의도적으로 타 도메인으로 보내야 하는 링크"를 그대로 둬야 하므로 정규화하지 않는다.
// 별칭 접속 시: 메인 도메인(www.myfirstguide.com) 절대주소나 선행 /v 가 들어오면 앱 상대경로(to)로 바꿔,
//   basePath(/v) 오류·myfirstguide 노출을 막는다. 그 외 호스트(creator/api/storage·외부)는 href 로 그대로 둔다.
const _appConfig = useAppConfig() as Record<string, any>
// 현재 요청이 별칭 도메인(domainAliases에 등록된 host)으로 들어왔는지 — 일반 도메인이면 정규화 자체를 하지 않는다.
const _isAliasDomain = useDYIsAliasDomain()

// 정규화 대상은 "별칭이 대체하는 메인 도메인"뿐. (creator/api/storage 등 서브도메인은 별칭 환경에서도 제외)
const _mainDomain = String(_appConfig.domain || '').toLowerCase()  // 예: www.myfirstguide.com
const _mainRoot = _mainDomain.replace(/^www\./, '')               // 예: myfirstguide.com
const _isMainHost = (host: string) => {
  const h = host.toLowerCase()
  return h === _mainDomain || (!!_mainRoot && h === _mainRoot)
}

const _resolved = computed<{ to?: any; href?: string }>(() => {
  // 별칭 도메인이 아니면 정규화하지 않고 원본 그대로 (일반 도메인 기존 동작 보존 — creator 등 타 도메인 링크 유지)
  if (!_isAliasDomain) return { to: props.to, href: props.href }

  // 라우터 location 객체 to 는 그대로 통과
  if (props.to && typeof props.to !== 'string') return { to: props.to }

  const rawTo = typeof props.to === 'string' ? props.to.trim() : ''
  const rawHref = typeof props.href === 'string' ? props.href.trim() : ''
  const raw = rawTo || rawHref
  if (!raw) return { to: props.to, href: props.href }

  // 절대 http(s) URL: 메인 도메인만 앱 상대경로(to)로, 그 외(서브도메인 creator 등·외부)는 href 그대로
  if (/^https?:\/\//i.test(raw)) {
    let u: URL
    try { u = new URL(raw) } catch { return { href: raw } }
    if (!_isMainHost(u.hostname)) return { href: raw }                      // 메인 도메인 외 → href 그대로
    let path = (u.pathname + u.search + u.hash).replace(/^\/v(?=\/|$)/, '')  // 도메인 제거 + 선행 /v 제거
    if (!path.startsWith('/')) path = '/' + path
    return { to: path }                                                      // 메인 도메인 → 앱 상대경로(to)
  }

  // 상대경로/앵커/기타 스킴: 원래 슬롯(to/href) 유지, 선행 /v 만 제거
  const cleaned = raw.replace(/^\/v(?=\/|$)/, '')
  if (rawTo) return { to: cleaned || raw }
  return { href: cleaned || raw }
})

const resolvedTo = computed(() => _resolved.value.to)
const resolvedHref = computed(() => _resolved.value.href)

const handleClickEvent = (event: Event) => {
  if (typeof resolvedTo.value === 'string' && resolvedTo.value === route.fullPath) {
    event.preventDefault()
  }

  if (resolvedHref.value === '#') {
    event.preventDefault()
  }

  emit('click', event)
}

</script>